import type { ComponentProps, Ref } from "react";
import { useState } from "react";
import { Button as TEUIButton } from "@timeedit/ui-components";
import type { TooltipProps } from "antd";
import { Button as AntdButton, Grid } from "antd";
import clsx from "clsx";
import TooltipDefault from "@components/tooltip/tooltipDefault.component";

export type ButtonDefaultProps = ComponentProps<typeof TEUIButton> & {
  tooltip?: string;
  tooltipPlacement?: TooltipProps["placement"];
  wrapperClassName?: string;
  isFullWidthOnSmall?: boolean;
  isNegativeMarginWhenLarge?: boolean;
  btnRef?: Ref<HTMLButtonElement | HTMLAnchorElement>;
};

/**
 * Button component with tooltip.
 * The tooltip is manually controlled because antd doesn't allow a dropdown with a tooltip.
 */
const ButtonDefault = ({
  tooltip,
  tooltipPlacement,
  wrapperClassName,
  isFullWidthOnSmall,
  isNegativeMarginWhenLarge,
  btnRef,
  ...rest
}: Readonly<ButtonDefaultProps>) => {
  const [isHovered, setIsHovered] = useState(false);

  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const isTabletOrSmaller = !screens.md;

  const wrapperClassNames = clsx(
    "sg-leading-[0px]",
    { "sg-w-full": isFullWidthOnSmall && isTabletOrSmaller }, // responsive
    { "-sg-my-2": isNegativeMarginWhenLarge && rest.size === "large" }, // prevent layout shift
    wrapperClassName
  );

  /**
   * const styleToHideTooltipOnHover = useMemo(
   *     (): CSSProperties => ({
   *       pointerEvents: "none"
   *     }),
   *     []
   *   );
   */

  return (
    <div
      className={wrapperClassNames}
      aria-label={tooltip}
      onMouseOver={() => setIsHovered(true)}
      onMouseOut={() => setIsHovered(false)}
      onClick={() => setIsHovered(false)}
    >
      <TooltipDefault
        title={tooltip}
        placement={tooltipPlacement}
        open={!!tooltip && isHovered}
        variant="ghost"
      >
        {btnRef ? (
          <AntdButton
            {...rest}
            block={isFullWidthOnSmall && isTabletOrSmaller}
            aria-label={tooltip}
            ref={btnRef}
          />
        ) : (
          //todo make TEUIButton accept ref
          <TEUIButton
            {...rest}
            block={isFullWidthOnSmall && isTabletOrSmaller}
            aria-label={tooltip}
          />
        )}
      </TooltipDefault>
    </div>
  );
};

export default ButtonDefault;
